
import { getConfig } from "../global-config";

let isRefreshing = false;
let consecutiveFailures = 0;
let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;

const BASE_POLL_INTERVAL = 90_000;       
const MAX_POLL_INTERVAL = 15 * 60_000;

type NewVersionHandler = (info: {
  currentVersion: string;
  latestVersion: string;
  reload: () => void;
}) => void;

const defaultNewVersionHandler: NewVersionHandler = ({ reload }) => reload();

export async function checkVersion(onNewVersion: NewVersionHandler = defaultNewVersionHandler) {
  // Skip if tab isn't visible — no point checking while user is away,
  if (document.visibilityState !== 'visible') {
    return;
  }

  const config = getConfig();

  try {
    const response = await fetch(`/config.json?t=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) {
      consecutiveFailures++;
      console.error('Failed to fetch config', response.status);
      return;
    }

    consecutiveFailures = 0;

    const latestConfig = await response.json();
    const latestVersion = latestConfig?.app?.version;
    const currentVersion = config.appVersion;

    if (
      latestVersion &&
      currentVersion &&
      latestVersion !== currentVersion &&
      !isRefreshing
    ) {
      isRefreshing = true;

      console.log(
        `New deployment detected: ${currentVersion} → ${latestVersion}`
      );

      onNewVersion({
        currentVersion,
        latestVersion,
        reload: () => window.location.reload(),
      });
    }
  } catch (error) {
    consecutiveFailures++;
    console.error('Version check failed', error);
  }
}

let hasStartedPolling = false;

export function checkConfigVersion(onNewVersion?: NewVersionHandler) {
  if (hasStartedPolling) return;
  hasStartedPolling = true;

  const scheduleNext = () => {
    // Exponential backoff: 90s → 3min → 6min → ... capped at 15min
    const interval = Math.min(
      BASE_POLL_INTERVAL * Math.pow(2, consecutiveFailures),
      MAX_POLL_INTERVAL
    );

    pollTimeoutId = setTimeout(async () => {
      await checkVersion(onNewVersion);
      scheduleNext();
    }, interval);
  };

  // Run once on tab regaining focus — catches the case where the user
  // came back after the app was idle through a deployment.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkVersion(onNewVersion);
    }
  });

  scheduleNext();
}

const CHUNK_RELOAD_FLAG = 'chunk-reload-attempted';

export function clearChunkReloadFlag() {
  sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
}

export function registerChunkErrorHandler() {
  const handleChunkError = (message: string) => {
    const isChunkError =
      message.includes('ChunkLoadError') ||
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Loading chunk') ||
      message.includes('Importing a module script failed');

    if (!isChunkError) return;

    // Guard against infinite reload loops.
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      console.error(
        'Chunk error after reload — not retrying. Likely network or asset issue.'
      );
      return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
    console.error('Chunk load error detected. Reloading app.');
    window.location.reload();
  };

  // Catches synchronous script load errors
  window.addEventListener('error', (event) => {
    handleChunkError(event?.message || '');
  });

  window.addEventListener('unhandledrejection', (event) => {
    const message = event?.reason?.message || String(event?.reason || '');
    handleChunkError(message);
  });
}